import type { CostSummaryPercentiles } from "@/util/cost-summary"

const BASE_URL =
  process.env.NEXT_PUBLIC_WEBSITE_HOST || "https://citizenshipper.com"

interface BreedReviewsData {
  transportServiceRatings?: string
  transportServiceReviews?: string
}

// The dog marketplace cost-per-mile band that backs the offer's per-mile detail.
// Breed pages have no breed-level cost data, so this is the marketplace-wide band
// from the committed dog snapshot — identical across breeds and clearly modelled
// as a per-mile UnitPriceSpecification, never a per-shipment price. See
// docs/adr/0004-dog-breed-cost-section.md.
function buildOffers(costPerMile?: CostSummaryPercentiles | null) {
  const offers: Record<string, unknown> = {
    "@type": "AggregateOffer",
    // Total-dollar per-shipment floor (the "from $X" snippet). Retained from the
    // long-standing breed schema; the marketplace band is per-mile and can't
    // honestly stand in for it.
    lowPrice: "245",
    priceCurrency: "USD",
  }
  if (costPerMile) {
    offers.priceSpecification = {
      "@type": "UnitPriceSpecification",
      price: costPerMile.typical.toFixed(2),
      priceCurrency: "USD",
      unitText: "mile",
      referenceQuantity: {
        "@type": "QuantitativeValue",
        value: "1",
        unitCode: "SMI",
      },
    }
  }
  return offers
}

export function getDogBreedRichSnippets(
  breed: string,
  breedSlug: string,
  reviewsData?: BreedReviewsData,
  heroImageUrl?: string,
  marketplaceCostPerMile?: CostSummaryPercentiles | null,
): Record<string, unknown>[] {
  const pageUrl = `${BASE_URL}/dog-shipping/${breedSlug}`
  const ratingValue = reviewsData?.transportServiceRatings || "4.9"
  const ratingCount = reviewsData?.transportServiceReviews || "7879"

  return [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: `${breed} Shipping Service`,
      image:
        heroImageUrl ||
        "https://a-us.storyblok.com/f/1002522/1440x685/6c4e37167f/premium-pet-transport-hero.png/m/",
      description: `Safe and reliable ${breed} transport service that delivers anywhere within the US.`,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue,
        ratingCount,
        bestRating: "5",
        worstRating: "1",
      },
      offers: buildOffers(marketplaceCostPerMile),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: BASE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Dog Transportation",
          item: `${BASE_URL}/dog-transportation`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "All Breeds",
          item: `${BASE_URL}/dog-shipping/all-breeds`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: breed,
          item: pageUrl,
        },
      ],
    },
  ]
}
